<template>
  <b-container fluid>
    <div class="d-flex flex-column align-items-center mt-3">
        <b-img
          src="../assets/logo-qr.png"
          alt="Hoops Yard"
          width="120"
          ref="logo"
          class="d-none"
        ></b-img>
    </div>
    <b-card>
      <template #header>
        <div class="d-flex align-items-center justify-content-between">
          <h6 class="mb-0">Su reserva con Hoops Yard:</h6>
          <h3 class="mb-0">
            <b-link
              href="https://wa.me/+50763084298"
              rel="noopener" target="_blank"
              class="btn btn-success rounded-circle text-inverse"
            >
              <strong class="icon-whatsapp" />
            </b-link>
          </h3>
        </div>
      </template>

      <div class="d-flex flex-column align-items-stretch">
        <template v-if="!isReady">
          <b-spinner></b-spinner>
        </template>
        <template v-else-if="recordNotFound">
          <b-alert show variant="danger">
            <p><strong>No podemos encontrar su reserva.</strong></p>
            <p>Por favor contactanos</p>
          </b-alert>
        </template>
        <template v-else>
          <h1 class="mb-4">{{ title }}</h1>
          <h6>Reservaciones:</h6>
          <b-list-group class="w-100">
            <template v-for="booking in bookings">
              <b-list-group-item
              :key="booking.id"
              :variant="booking.isPassed ? 'light' : ''"
              >
                <b-row
                >
                  <b-col>
                    <strong>{{ booking.day }}</strong>
                  </b-col>
                  <b-col class="border-left border-right">
                    <strong>{{ booking.start }}</strong>
                    <span><br>{{ booking.end }}</span>
                  </b-col>
                  <b-col>
                    <div class="mx-n1">
                      <div><!-- {{ booking.productId }} --><strong>
                        <template v-if="booking.productId == 72">Cancha exterior</template>
                        <template v-else>Cancha PVC</template>
                      </strong></div>
                      <div class="hstack gap-1">
                        <SportEmoji class="mx-1" :value="booking.sport" />
                        <template v-if="booking.scoreboard">
                          ⏱️
                        </template>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </b-list-group-item>
            </template>
          </b-list-group>
          <h6 class="mt-3">Estado del pago:</h6>
          <b-progress :max="record.amount" height="1.75rem" show-value>
            <b-progress-bar :value="paymentsBar.success" variant="success">
              <strong>Pagado</strong>
            </b-progress-bar>
            <b-progress-bar :value="paymentsBar.warning" variant="warning">
              <strong>${{paymentsBar.warning | amount}} pendientes</strong>
            </b-progress-bar>
            <b-progress-bar :value="paymentsBar.danger" variant="danger">
              <strong>${{paymentsBar.danger | amount}} pendientes</strong>
            </b-progress-bar>
          </b-progress>
          <div class="mt-4 text-center">
            <template v-if="!isQRReady">
              <b-spinner></b-spinner>
            </template>
            <template v-else>
              <QRCanvas :options="qrOptions"></QRCanvas>
              <p><small class="text-muted">
                {{ qrOptions.data }}
              </small></p>
            </template>
          </div>
        </template>
      </div>
    </b-card>
  </b-container>
</template>
<script>
import { format, isBefore, startOfDay, add, compareAsc } from 'date-fns'
import { Record } from '@/models/Record'
import { getRecord } from '@/firebase-firestore'
import { mapGetters, mapActions } from 'vuex'
import SportEmoji from '@/components/booking/SportEmoji.vue'
const { QRCanvas } = require('qrcanvas-vue')
export default {
  components: {
    SportEmoji,
    QRCanvas
  },
  beforeMount () {
    this.getProducts()
  },
  mounted () {
    const image = new Image()
    const src = this.$refs.logo.src
    image.src = src
    image.onload = () => {
      this.qrOptions.logo = { image }
    }
  },
  watch: {
    isProductsReady: {
      handler: function async (isReady) {
        if (isReady) {
          const { id } = this.$route.params
          if (id) {
            getRecord(id)
              .then(row => {
                if (row) {
                  this.record = new Record(row)
                  this.qrOptions.data = document.domain + this.$route.path
                } else {
                  this.recordNotFound = true
                }
              })
          }
        }
      },
      immediate: true
    }
  },
  data () {
    return {
      recordNotFound: false,
      recordPassed: false,
      record: null,
      qrOptions: {
        padding: 8,
        cellSize: 7,
        correctLevel: 'H',
        logo: null,
        data: null
      },
      logoSrc: '~img/logo.png'
    }
  },
  computed: {
    ...mapGetters('products', {
      isProductsReady: 'isProductsReady',
      getProduct: 'getProduct'
    }),
    isReady () {
      return Boolean(this.record || this.recordNotFound)
    },
    isQRReady () {
      return Boolean(this.qrOptions.logo && this.qrOptions.data)
    },
    recordProduct () {
      return this.getProduct(this.record.productId)
    },
    paid () {
      return this.record?.payments
        .reduce((sum, p) => sum + p.amount, 0)
    },
    paymentsBar () {
      const leftToPay = this.record?.amount - this.paid
      return {
        success: this.paid,
        warning: this.paid > 0 ? leftToPay : 0,
        danger: this.paid === 0 ? leftToPay : 0
      }
    },
    title () {
      return this.isReady
        ? typeof this.record.title === 'string' && this.record.title.trim().length
          ? this.record.title
          : this.recordProduct
            ? this.recordProduct.productName
            : null
        : null
    },
    bookings () {
      const results = this.isReady && Array.isArray(this.record.bookings)
        ? this.record.bookings
          .map(booking => {
            const product = this.getProduct(booking.productId)
            return {
              dateStart: booking.dateStart,
              day: format(booking.dateStart, 'EEEE do LLL.'),
              start: format(booking.dateStart, 'p'),
              end: format(add(booking.dateEnd, { seconds: 1 }), 'p'),
              court: product ? product.productName : null,
              productId: booking.productId,
              sport: booking.sport,
              scoreboard: booking.scoreboard,
              isPassed: isBefore(booking.dateStart, startOfDay(new Date()))
            }
          })
        : []
      results.sort((a, b) => {
        return compareAsc(a.dateStart, b.dateStart)
      })
      return results
    }
  },
  methods: {
    ...mapActions('products', {
      getProducts: 'getProducts'
    })
  }
}
</script>
